import React, { useEffect, useState } from 'react';
import { FiDownload, FiShare2, FiX } from 'react-icons/fi';

const DISMISS_KEY = 'worklogz_install_banner_dismissed';

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

/**
 * Prompts users to install Worklogz as an app (PWA).
 * Chrome/Edge: native beforeinstallprompt. iOS: Add to Home Screen tip.
 */
const InstallAppBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
      setIosTip(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    if (isIos()) {
      setIosTip(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
    setDeferredPrompt(null);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/15 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <img
            src="/worklogz-logo.png"
            alt=""
            className="h-11 w-11 rounded-xl object-contain bg-[#f4f7fa] border border-slate-100 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#1a365d] text-sm">Install Worklogz App</p>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              {iosTip
                ? 'Add to your Home Screen for quick invoice billing on mobile.'
                : 'Install for a full-screen, mobile-friendly billing experience.'}
            </p>
            {iosTip ? (
              <p className="text-[11px] text-slate-600 mt-2 flex items-start gap-1.5">
                <FiShare2 className="shrink-0 mt-0.5 text-teal-600" />
                <span>
                  Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>
                </span>
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"
            aria-label="Dismiss"
          >
            <FiX size={18} />
          </button>
        </div>
        <div className="flex gap-2 px-4 pb-4">
          {!iosTip && deferredPrompt ? (
            <button
              type="button"
              onClick={handleInstall}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a365d] text-white text-sm font-semibold py-2.5 hover:bg-[#153050]"
            >
              <FiDownload /> Install App
            </button>
          ) : null}
          <button
            type="button"
            onClick={dismiss}
            className={`${
              !iosTip && deferredPrompt ? 'px-4' : 'flex-1'
            } rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold py-2.5 hover:bg-slate-50`}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallAppBanner;
